import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CheckCircle2,
  Clock3,
  Code2,
  Layers3,
  MessageCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  Wand2,
} from 'lucide-react';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import { localizeService, services as fallbackServiceData } from '../data/marketplace';
import useServiceCategories from '../data/useServiceCategories';
import './Home.css';

const coverIcons = [Code2, Layers3, Wand2, BriefcaseBusiness, Sparkles, Users];

const copy = {
  zh: {
    heroKicker: '技能服务与需求协作平台',
    heroTitle: '找到合适的专家，把需求稳定交付',
    heroLead: 'Oblitrok 帮客户发布需求、预约服务、托管付款，并让技术员在平台内沟通、交稿、结算。',
    searchPlaceholder: '搜索网站开发、自动化脚本、Logo 设计...',
    searchButton: '搜索服务',
    postRequirement: '发布需求',
    postService: '发布服务',
    popular: '热门',
    tags: ['网站开发', 'Logo 设计', '自动化脚本', '数据看板', '小程序原型', '品牌视觉'],
    categoryTitle: '按分类浏览',
    categorySub: '从常用服务类型里快速定位需求，也可以直接搜索具体技能。',
    allCategories: '全部分类',
    serviceTitle: '热门技能服务',
    serviceSub: '这些服务来自后台已发布数据，可直接查看详情、沟通和下单。',
    viewAll: '查看全部服务',
    noServices: '暂无已发布服务。请先让技术员发布真实服务，审核通过后会显示在这里。',
    loading: '正在加载已发布服务...',
    verified: '已认证',
    orders: '单',
    remote: '远程',
    viewService: '查看服务',
    providerTitle: '精选服务者',
    providerSub: '根据已发布服务自动汇总，避免首页空白。',
    providerFallbackRole: '平台服务者',
    noProviders: '暂无服务者资料',
    stepsTitle: '交易如何闭环',
    steps: [
      ['发布或选择服务', '客户描述目标、预算、时间和验收标准，技术员提供清晰报价。'],
      ['平台内沟通付款', '双方在消息中确认范围，客户通过平台支付，资金先进入托管状态。'],
      ['交付验收结算', '技术员交稿，客户确认收货后，平台按分成规则结算到技术员余额。'],
    ],
  },
  en: {
    heroKicker: 'Skill services and requirement collaboration',
    heroTitle: 'Find the right expert and get work delivered',
    heroLead: 'Oblitrok helps buyers post requirements, book services, hold payments in escrow, and keep delivery, messages, and settlement inside one platform.',
    searchPlaceholder: 'Search web development, automation, logo design...',
    searchButton: 'Search services',
    postRequirement: 'Post requirement',
    postService: 'Post service',
    popular: 'Popular',
    tags: ['Web development', 'Logo design', 'Automation scripts', 'Data dashboard', 'App prototype', 'Brand identity'],
    categoryTitle: 'Browse by category',
    categorySub: 'Start from common service types or search for a specific skill.',
    allCategories: 'All categories',
    serviceTitle: 'Popular skill services',
    serviceSub: 'These services come from live published data and can be opened, discussed, and ordered.',
    viewAll: 'View all services',
    noServices: 'No published services yet. Ask providers to post real services first.',
    loading: 'Loading published services...',
    verified: 'Verified',
    orders: 'orders',
    remote: 'Remote',
    viewService: 'View service',
    providerTitle: 'Featured providers',
    providerSub: 'Generated from published services so the homepage stays useful.',
    providerFallbackRole: 'Platform provider',
    noProviders: 'No provider profiles yet',
    stepsTitle: 'How the transaction closes',
    steps: [
      ['Post or choose a service', 'The buyer defines scope, budget, timeline, and acceptance criteria.'],
      ['Chat and pay on-platform', 'Both sides confirm the scope in messages, then payment is held in escrow.'],
      ['Deliver, accept, settle', 'The provider submits delivery. After buyer acceptance, funds settle to the provider balance.'],
    ],
  },
};

function getPriceValue(price = '') {
  const matched = String(price).replace(/,/g, '').match(/\d+/);
  return matched ? Number(matched[0]) : 0;
}

function formatServiceLink(service) {
  if (service.liveId) return `/services/listing-${service.liveId}`;
  return `/services/${service.id}`;
}

function normalizeService(item = {}, source = 'live') {
  return {
    ...item,
    id: source === 'live' ? `listing-${item.id}` : `local-${item.id}`,
    liveId: source === 'live' ? item.id : null,
    summary: item.summary || item.description || '',
    provider: item.provider || item.provider_name || '',
    tags: Array.isArray(item.tags) ? item.tags : [],
    orders: Number(item.orders || 0),
    rating: Number(item.rating || 0),
  };
}

function mergeServices(liveItems, fallbackItems) {
  const liveServices = liveItems.map(item => normalizeService(item, 'live'));
  const liveCategories = new Set(liveServices.map(service => service.category).filter(Boolean));
  const fallbackServices = fallbackItems
    .filter(service => !liveCategories.has(service.category))
    .map(service => normalizeService(service, 'fallback'));
  return [...liveServices, ...fallbackServices];
}

export default function Home() {
  const { language, localizeCategory, localizeMethod } = useLanguage();
  const configuredCategories = useServiceCategories();
  const navigate = useNavigate();
  const text = copy[language] || copy.en;
  const [keyword, setKeyword] = useState('');
  const [liveServices, setLiveServices] = useState([]);
  const fallbackServices = useMemo(
    () => fallbackServiceData.map(service => localizeService(service, language)),
    [language],
  );
  const displayServices = useMemo(
    () => mergeServices(liveServices, fallbackServices),
    [fallbackServices, liveServices],
  );

  useEffect(() => {
    let cancelled = false;
    api.get('/marketplace/services', {
      params: { sort: 'best', page: 1, limit: 50 },
      timeout: 4500,
    })
      .then(res => {
        if (!cancelled) setLiveServices(res.data.services || []);
      })
      .catch(() => {
        if (!cancelled) setLiveServices([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredServices = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    return displayServices.filter(service => {
      const searchable = [
        service.title,
        service.provider,
        service.summary,
        service.description,
        service.area,
        ...(service.tags || []),
        localizeCategory(service.category),
        localizeMethod(service.method),
      ].join(' ').toLowerCase();
      const byKeyword = !query || searchable.includes(query);
      return byKeyword;
    });
  }, [displayServices, keyword, localizeCategory, localizeMethod]);

  const providerCards = useMemo(() => {
    const providers = new Map();
    displayServices.forEach(service => {
      const name = String(service.provider || '').trim();
      if (!name) return;
      const existing = providers.get(name) || {
        name,
        role: localizeCategory(service.category) || text.providerFallbackRole,
        orders: 0,
        ratingTotal: 0,
        ratingCount: 0,
        lowestPrice: 0,
      };
      existing.orders += Number(service.orders || 0);
      if (Number(service.rating || 0) > 0) {
        existing.ratingTotal += Number(service.rating);
        existing.ratingCount += 1;
      }
      const servicePrice = getPriceValue(service.price);
      existing.lowestPrice = existing.lowestPrice ? Math.min(existing.lowestPrice, servicePrice) : servicePrice;
      providers.set(name, existing);
    });

    return Array.from(providers.values())
      .map(provider => ({
        ...provider,
        rating: provider.ratingCount ? (provider.ratingTotal / provider.ratingCount).toFixed(1) : '5.0',
      }))
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 4);
  }, [displayServices, localizeCategory, text.providerFallbackRole]);

  const categoryTiles = configuredCategories.slice(0, 6);
  const serviceCards = useMemo(() => {
    const selected = [];
    const selectedIds = new Set();

    for (const category of categoryTiles) {
      const service = filteredServices.find(item => item.category === category.name);
      if (service) {
        selected.push(service);
        selectedIds.add(service.id);
      }
    }

    const additional = filteredServices.filter(service => !selectedIds.has(service.id));
    return [...selected, ...additional].slice(0, 8);
  }, [categoryTiles, filteredServices]);

  const submitSearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (keyword.trim()) params.set('keyword', keyword.trim());
    navigate(`/services${params.toString() ? `?${params}` : ''}`);
  };

  return (
    <div className="home-page">
      <section className="home-hero">
        <div className="home-hero-media" aria-hidden="true" />
        <div className="home-hero-content">
          <span className="home-kicker"><Sparkles size={16} />{text.heroKicker}</span>
          <h1>{text.heroTitle}</h1>
          <p>{text.heroLead}</p>

          <form className="home-search" onSubmit={submitSearch}>
            <Search size={20} aria-hidden="true" />
            <input value={keyword} onChange={event => setKeyword(event.target.value)} placeholder={text.searchPlaceholder} />
            <button type="submit">{text.searchButton}</button>
          </form>

          <div className="home-tags">
            <span>{text.popular}</span>
            {text.tags.map(tag => (
              <button key={tag} type="button" onClick={() => setKeyword(tag)}>{tag}</button>
            ))}
          </div>

          <div className="home-actions">
            <Link to="/tasks/create">{text.postRequirement}<ArrowRight size={17} /></Link>
            <Link to="/services/new">{text.postService}</Link>
          </div>

        </div>
      </section>

      <section className="home-section home-categories">
        <div className="home-section-head">
          <div>
            <h2>{text.categoryTitle}</h2>
            <p>{text.categorySub}</p>
          </div>
          <Link to="/category">{text.allCategories}<ArrowRight size={16} /></Link>
        </div>

        <div className="home-category-grid">
          {categoryTiles.map((cat, index) => {
            const Icon = coverIcons[index % coverIcons.length];
            return (
              <Link
                key={cat.name}
                to={`/services?categories=${encodeURIComponent(cat.name)}`}
                className="home-category-card"
              >
                <span className={`home-category-icon category-tone-${index % 6}`}><Icon size={22} /></span>
                <strong>{localizeCategory(cat.name)}</strong>
                <small>{cat.items.map(item => localizeCategory(item)).slice(0, 3).join(' / ')}</small>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-head">
          <div>
            <h2>{text.serviceTitle}</h2>
            <p>{text.serviceSub}</p>
          </div>
          <Link to="/services">{text.viewAll}<ArrowRight size={16} /></Link>
        </div>

        <div className="home-service-grid">
          {serviceCards.map((service, index) => {
            const Icon = coverIcons[index % coverIcons.length];
            return (
              <Link key={service.id} to={formatServiceLink(service)} className="home-service-card">
                <div className={`home-service-cover service-tone-${index % 6}`}>
                  <span>{localizeCategory(service.category)}</span>
                  <Icon size={44} strokeWidth={1.8} />
                  <i />
                  <b />
                </div>
                <div className="home-service-body">
                  <strong className="home-price">{service.price}</strong>
                  <h3>{service.title}</h3>
                  <p>{service.summary}</p>
                  <div className="home-service-meta">
                    <span><Clock3 size={14} />{localizeMethod(service.method)}</span>
                    <span><BadgeCheck size={14} />{text.verified}</span>
                  </div>
                  <div className="home-service-foot">
                    <span><Star size={14} />{service.rating || '5.0'} · {service.orders} {text.orders}</span>
                    <em>{text.viewService}</em>
                  </div>
                </div>
              </Link>
            );
          })}
          {serviceCards.length === 0 && <div className="home-empty">{text.noServices}</div>}
        </div>
      </section>

      <section className="home-section home-provider-section">
        <div className="home-section-head">
          <div>
            <h2>{text.providerTitle}</h2>
            <p>{text.providerSub}</p>
          </div>
          <Link to="/freelancers">{text.providerTitle}<ArrowRight size={16} /></Link>
        </div>

        <div className="home-provider-grid">
          {providerCards.map((provider, index) => (
            <Link key={provider.name} to="/freelancers" className="home-provider-card">
              <div className={`home-provider-avatar provider-tone-${index % 6}`}>
                <Users size={28} />
              </div>
              <h3>{provider.name}</h3>
              <p>{provider.role}</p>
              <div>
                <span><Star size={14} />{provider.rating}</span>
                <span>{provider.orders} {text.orders}</span>
              </div>
            </Link>
          ))}
          {providerCards.length === 0 && <div className="home-empty">{text.noProviders}</div>}
        </div>
      </section>

      <section className="home-section home-steps">
        <div className="home-section-head">
          <div>
            <h2>{text.stepsTitle}</h2>
          </div>
        </div>
        <div className="home-step-grid">
          {text.steps.map(([title, body], index) => (
            <article key={title}>
              <span>{index + 1}</span>
              <h3>{title}</h3>
              <p>{body}</p>
              {index === 0 && <CheckCircle2 size={20} aria-hidden="true" />}
              {index === 1 && <ShieldCheck size={20} aria-hidden="true" />}
              {index === 2 && <MessageCircle size={20} aria-hidden="true" />}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
